import axios from "utils/axios";
import * as types from "./ActionTypes";
import { API_URL } from "configs";

const loader = (label) => ({
  type: types.SHOW_LOADER,
  common_loader_label: label,
});

const hideLoader = () => ({
  type: types.HIDE_LOADER,
});

const snackbar = (msg, type = "general_error") => ({
  type: types.SHOW_SNACKBAR,
  snackbar_notification: msg,
  snackbar_notification_type: type,
});

const fetchSuccess = (type, listKey) => (data, meta = null) => ({
  type,
  [listKey]: data,
  meta,
});

const getSuccess = (type) => (data, action) => ({
  type,
  record: data,
  actionType: action,
});

const handleError = (error, dispatch, validationType) => {
  const res = error?.response;
  if (res?.status === 422) {
    dispatch({
      type: validationType,
      validation_error: res?.data?.errors || {},
    });
    return;
  }

  dispatch(
    snackbar(
      res?.data?.error || res?.data?.message || `${res?.status} ${res?.statusText}`
    )
  );
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const normalizeEnvelope = (res) => {
  const envelope = res?.data || {};
  return {
    success: envelope.success,
    message: envelope.message,
    data: envelope?.data ?? null,
    errors: envelope?.errors ?? null,
    meta: envelope?.meta ?? null,
  };
};

export const invoiceConfigurationRedirectToList = () => ({
  type: types.INVOICE_CONFIG_REDIRECT_TO_LIST,
});

export const hideInvoiceConfigurationValidationError = (fieldKey) => ({
  type: types.HIDE_INVOICE_CONFIG_FIELD_VALIDATION_ERROR,
  field_key: fieldKey,
});

export const fetchInvoiceConfigurations =
  (params = {}, object_viewed_id = "", options = {}) =>
  (dispatch) => {
    const isSilent = Boolean(options?.silent);

    if (!isSilent) {
      dispatch({ type: types.INVOICE_CONFIG_REQUEST });
      dispatch(loader());
    }

    return axios
      .get(`${API_URL}invoice-configurations`, {
        params: {
          ...params,
          object_viewed_id,
        },
        headers: authHeaders(),
      })
      .then((res) => {
        if (!isSilent) {
          dispatch(hideLoader());
        }
        const envelope = normalizeEnvelope(res);
        dispatch(
          fetchSuccess(types.INVOICE_CONFIG_SUCCESS, "invoiceConfigurationsList")(
            envelope.data,
            envelope.meta
          )
        );
        return Promise.resolve(envelope);
      })
      .catch((err) => {
        if (!isSilent) {
          dispatch(hideLoader());
        }
        handleError(err, dispatch, types.INVOICE_CONFIG_VALIDATION_ERROR);
        return Promise.reject(err);
      });
  };

export const getInvoiceConfigurationById =
  (id, actionType = "view", object_viewed_id = "") =>
  (dispatch) => {
    dispatch({ type: types.INVOICE_CONFIG_REQUEST });
    dispatch(loader());

    return axios
      .get(`${API_URL}invoice-configurations/${id}`, {
        params: { object_viewed_id },
        headers: authHeaders(),
      })
      .then((res) => {
        dispatch(hideLoader());
        const envelope = normalizeEnvelope(res);
        dispatch(getSuccess(types.INVOICE_CONFIG_GET_SUCCESS)(envelope.data, actionType));
        return Promise.resolve(envelope);
      })
      .catch((err) => {
        dispatch(hideLoader());
        handleError(err, dispatch, types.INVOICE_CONFIG_VALIDATION_ERROR);
        return Promise.reject(err);
      });
  };

export const createInvoiceConfiguration =
  (data, object_viewed_id = "") =>
  (dispatch) => {
    dispatch({ type: types.INVOICE_CONFIG_REQUEST });
    dispatch(loader("Saving configuration..."));

    return axios
      .post(`${API_URL}invoice-configurations`, data, {
        params: { object_viewed_id },
        headers: authHeaders(),
      })
      .then((res) => {
        dispatch(hideLoader());
        const envelope = normalizeEnvelope(res);
        dispatch(snackbar(envelope.message || "Configuration saved successfully", "success"));
        dispatch(getSuccess(types.INVOICE_CONFIG_GET_SUCCESS)(envelope.data, "update"));
        return Promise.resolve(envelope);
      })
      .catch((err) => {
        dispatch(hideLoader());
        handleError(err, dispatch, types.INVOICE_CONFIG_VALIDATION_ERROR);
        return Promise.reject(err);
      });
  };

export const updateInvoiceConfiguration =
  (id, data, object_viewed_id = "") =>
  (dispatch) => {
    dispatch({ type: types.INVOICE_CONFIG_REQUEST });
    dispatch(loader("Updating configuration..."));

    return axios
      .put(`${API_URL}invoice-configurations/${id}`, data, {
        params: { object_viewed_id },
        headers: authHeaders(),
      })
      .then((res) => {
        dispatch(hideLoader());
        const envelope = normalizeEnvelope(res);
        dispatch(snackbar(envelope.message || "Configuration updated successfully", "success"));
        dispatch(getSuccess(types.INVOICE_CONFIG_GET_SUCCESS)(envelope.data, "update"));
        return Promise.resolve(envelope);
      })
      .catch((err) => {
        dispatch(hideLoader());
        handleError(err, dispatch, types.INVOICE_CONFIG_VALIDATION_ERROR);
        return Promise.reject(err);
      });
  };

export const toggleInvoiceConfigurationSections =
  (id, sectionStates = [], object_viewed_id = "") =>
  (dispatch) => {
    dispatch({ type: types.INVOICE_CONFIG_REQUEST });

    return axios
      .patch(
        `${API_URL}invoice-configurations/${id}/sections`,
        { section_states: sectionStates },
        {
          params: { object_viewed_id },
          headers: authHeaders(),
        }
      )
      .then((res) => {
        const envelope = normalizeEnvelope(res);
        dispatch(snackbar(envelope.message || "Sections updated", "success"));
        return Promise.resolve(envelope);
      })
      .catch((err) => {
        handleError(err, dispatch, types.INVOICE_CONFIG_VALIDATION_ERROR);
        return Promise.reject(err);
      });
  };

export const cloneInvoiceConfiguration =
  (id, data = {}, object_viewed_id = "") =>
  (dispatch) => {
    dispatch({ type: types.INVOICE_CONFIG_REQUEST });
    dispatch(loader("Cloning configuration..."));

    return axios
      .post(`${API_URL}invoice-configurations/${id}/clone`, data, {
        params: { object_viewed_id },
        headers: authHeaders(),
      })
      .then((res) => {
        dispatch(hideLoader());
        const envelope = normalizeEnvelope(res);
        dispatch(snackbar(envelope.message || "Configuration cloned successfully", "success"));
        dispatch(invoiceConfigurationRedirectToList());
        return Promise.resolve(envelope);
      })
      .catch((err) => {
        dispatch(hideLoader());
        handleError(err, dispatch, types.INVOICE_CONFIG_VALIDATION_ERROR);
        return Promise.reject(err);
      });
  };

export const deleteInvoiceConfiguration =
  (id, object_viewed_id = "") =>
  (dispatch) => {
    dispatch({ type: types.INVOICE_CONFIG_REQUEST });
    dispatch(loader("Deleting configuration..."));

    return axios
      .delete(`${API_URL}invoice-configurations/${id}`, {
        params: { object_viewed_id },
        headers: authHeaders(),
      })
      .then((res) => {
        dispatch(hideLoader());
        const envelope = normalizeEnvelope(res);
        dispatch(snackbar(envelope.message || "Configuration deleted successfully", "success"));
        dispatch(invoiceConfigurationRedirectToList());
        return Promise.resolve(envelope);
      })
      .catch((err) => {
        dispatch(hideLoader());
        handleError(err, dispatch, types.INVOICE_CONFIG_VALIDATION_ERROR);
        return Promise.reject(err);
      });
  };
